// src/components/SecurityAlert.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiShield, FiAlertTriangle, FiX } from 'react-icons/fi';

const AlertOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const AlertContainer = styled.div`
  width: 90%;
  max-width: 500px;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border: 1px solid ${({ theme, severity }) =>
        severity === 'critical' ? theme.colors.error : theme.colors.primary};
  box-shadow: 0 0 20px ${({ theme, severity }) =>
        severity === 'critical' ? `${theme.colors.error}50` : `${theme.colors.primary}50`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[5]};
  animation: glitch 0.3s ease-in-out;
  
  @keyframes glitch {
    0% { transform: translate(-2px, 2px); }
    25% { transform: translate(2px, -2px); }
    50% { transform: translate(-2px, -2px); }
    75% { transform: translate(2px, 2px); }
    100% { transform: translate(0, 0); }
  }
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding-bottom: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const AlertTitle = styled.h3`
  display: flex;
  align-items: center;
  font-family: 'Courier New', monospace;
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  color: ${({ theme, severity }) =>
        severity === 'critical' ? theme.colors.error : theme.colors.primary};
  margin: 0;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  
  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const AlertContent = styled.div`
  font-family: 'Courier New', monospace;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.5;
`;

const AlertCode = styled.div`
  font-family: 'Courier New', monospace;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border-left: 3px solid ${({ theme, severity }) =>
        severity === 'critical' ? theme.colors.error : theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  span.highlight {
    color: ${({ theme, severity }) =>
        severity === 'critical' ? theme.colors.error : theme.colors.primary};
  }
`;

const ActionButton = styled.button`
  width: 100%;
  background: ${({ theme, severity }) =>
        severity === 'critical' ? theme.colors.error : theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  font-family: 'Courier New', monospace;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px ${({ theme, severity }) =>
        severity === 'critical' ? `${theme.colors.error}40` : `${theme.colors.primary}40`};
  }
`;

const SecurityAlert = ({
    isOpen,
    onClose,
    severity = 'warning',
    title,
    message,
    code,
    buttonText = 'Acknowledge'
}) => {
    const [visible, setVisible] = useState(isOpen);

    useEffect(() => {
        setVisible(isOpen);
    }, [isOpen]);

    if (!visible) return null;

    return (
        <AlertOverlay>
            <AlertContainer severity={severity}>
                <AlertHeader>
                    <AlertTitle severity={severity}>
                        {severity === 'critical' ? <FiAlertTriangle /> : <FiShield />}
                        {title || (severity === 'critical' ? 'SECURITY BREACH DETECTED' : 'SECURITY ALERT')}
                    </AlertTitle>
                    <CloseButton onClick={onClose}>
                        <FiX />
                    </CloseButton>
                </AlertHeader>

                <AlertContent>
                    {message || 'Unauthorized activity detected. This incident has been logged.'}
                </AlertContent>

                {code && (
                    <AlertCode severity={severity}>
                        <span className="highlight">&gt;</span> {code}
                    </AlertCode>
                )}

                <ActionButton severity={severity} onClick={onClose}>
                    {buttonText}
                </ActionButton>
            </AlertContainer>
        </AlertOverlay>
    );
};

export default SecurityAlert;